import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { BarChart, RotateCw, Save, AlertTriangle, Cpu, TrendingUp } from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';
import { BES_STAGES, ACTIONS } from '../../../utils/besCalculator';

export default function AdminBESView() {
  const { fetchWithAuth } = useAuth();
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [besConfig, setBesConfig] = useState<{actions: any} | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<number | string | null>(null);

  const fetchWorkspaces = async () => {
    setLoading(true);
    try {
      const [resWs, resConfig] = await Promise.all([
        fetchWithAuth('/api/workspaces'),
        fetchWithAuth('/api/admin/bes-config')
      ]);

      if (resWs.ok) {
        const data = await resWs.json();
        setWorkspaces(Array.isArray(data) ? data : []);
      }

      if (resConfig.ok) {
        const data = await resConfig.json();
        const payload = data?.data ?? data;
        setBesConfig(payload?.actions ? payload : { actions: ACTIONS });
      } else {
        setBesConfig({ actions: ACTIONS });
      }
    } catch (err) {
      console.error(err);
      showError("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const saveConfig = async () => {
    setSaving('config');
    try {
      const res = await fetchWithAuth('/api/admin/bes-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: besConfig })
      });
      
      if (res.ok) {
        showSuccess("Configurações salvas!");
      } else {
        showError("Falha ao salvar.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div id="admin-bes-module" className="w-full mx-auto pb-12 space-y-6 animate-in fade-in duration-500 max-w-[1400px]">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Cpu size={14} /> Cyzor Core Engine / BES
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Configuração Global BES
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Ajuste os pesos dos eventos que compõem o algoritmo do Business Event Score (SaaS Health).
          </p>
        </div>
        <button
          onClick={fetchWorkspaces}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-semibold shadow-xs transition-all"
        >
          <RotateCw size={14} className={loading ? 'animate-spin' : ''} /> Recarregar
        </button>
      </div>
      
      {/* Configuration Section */}
      {besConfig && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-xs p-6 space-y-6">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Pesos do Algoritmo (Pontuação por Ação)</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">
              Configure os pesos para cada evento gerado automaticamente pela plataforma. Os níveis (Ideia, MVP, Operação, Escala) são calculados pelo acúmulo desses eventos.
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Object.entries(besConfig.actions).map(([key, value]) => (
              <div key={key} className="space-y-1.5">
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{key}</label>
                <input 
                  type="number" 
                  value={value as number}
                  onChange={(e) => setBesConfig({...besConfig, actions: {...besConfig.actions, [key]: parseInt(e.target.value) || 0}})}
                  className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-gray-900 rounded-xl px-3 py-2 text-xs font-mono font-bold text-gray-950 transition outline-none"
                />
              </div>
            ))}
          </div>

          <button 
            onClick={saveConfig}
            disabled={saving === 'config'}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl transition text-xs font-semibold shadow-xs cursor-pointer mt-4"
          >
            {saving === 'config' ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar Configurações
          </button>
        </div>
      )}

      {/* Workspaces List */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xs overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col gap-1 bg-white">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Monitoramento de BES (Leitura)</h3>
          <p className="text-xs text-gray-500">O Score não pode ser alterado manualmente. Esta visão exibe os scores consolidados atuais.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="text-[11px] text-gray-400 uppercase bg-gray-50/60 border-b border-gray-100 font-semibold tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold">Workspace</th>
                <th className="px-6 py-4 font-semibold text-right">Score Atual (BES)</th>
                <th className="px-6 py-4 font-semibold text-right">Nível Derivado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {loading && workspaces.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                    <span className="text-xs text-gray-400 mt-2 block">Carregando score dos workspaces...</span>
                  </td>
                </tr>
              ) : workspaces.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-500 font-medium">
                    Nenhum workspace encontrado.
                  </td>
                </tr>
              ) : (
                workspaces.map((ws) => {
                  const bes = ws.settings?.besScore || 0;
                  const currentStage = BES_STAGES.find(s => bes >= s.min && bes <= s.max) || BES_STAGES[0];
                  return (
                    <tr key={ws.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-6 py-3.5 font-bold text-gray-900">{ws.name}</td>
                      <td className="px-6 py-3.5 text-right font-mono font-bold text-indigo-600">{bes.toLocaleString()}</td>
                      <td className="px-6 py-3.5 text-right">
                        <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider">
                          {currentStage.label}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
